/**
 * O cartão "Números da equipe" da aba WhatsApp de Integrações (protótipo v2, realinhamento de
 * 28/09/2026): uma linha por usuário **ativo**, com as iniciais, o nome e a situação do número —
 * conectado, pareando ou desconectado. Nunca o número em si: a gestão só enxerga a situação
 * (FR-71), e é o que `GET /numeros` devolve de útil aqui.
 *
 * **Só para o Admin de fábrica, e a exceção à convenção "esconder não é autorizar" é do serviço.**
 * As duas leituras que o cartão compõe — `GET /numeros` (`gerir_usuarios`, 11.1) e
 * `GET /usuarios` (Admin) — respondem `403` a todo mundo mais, inclusive ao Gestor comercial que
 * o protótipo mostra vendo o cartão. Um cartão montado para ele mostraria só a recusa, sem nada
 * que ele pudesse fazer; é o mesmo desenho de `ProntidaoDeGoLive`, e pela mesma razão. O componente
 * devolve `null` e as consultas nascem `habilitada: false`.
 *
 * **Quem nunca pediu pareamento não tem linha em `GET /numeros`**, e aparece como desconectado:
 * para quem olha o cartão, o chip dessa pessoa não atende — e é isso que a palavra diz.
 *
 * As duas fontes são compostas aqui, no SPA (AD-25). Uma fora do ar degrada só o que depende
 * dela: sem a lista de usuários não há linha nenhuma, e o cartão mostra a recusa; sem a situação,
 * as linhas existem e a coluna diz "indisponível".
 */

import { useId } from 'react'

import { type SituacaoDoWhatsapp, useSituacaoDoWhatsapp } from '@/modulos/comunicacao/api'
import { ehAdminDeFabrica, useUsuarios } from '@/modulos/identidade/api'
import { useSessao } from '@/modulos/identidade/sessao'
import { iniciaisDoNome } from '@/shell/iniciais'
import { ErroDaLeitura } from './Credencial'
import './integracoes.css'

export const TITULO_DA_EQUIPE = 'Números da equipe'
export const NOTA_DA_EQUIPE =
  'Cada pessoa conecta o próprio número; a gestão só enxerga a situação.'
const CARREGANDO = 'Carregando os números da equipe…'
const SEM_USUARIOS = 'Nenhum usuário ativo.'
const INDISPONIVEL = 'indisponível'

/** As palavras do protótipo: minúsculas, ao lado do ponto colorido. */
/** Desde a 13.21a1 a `situacao` é anulável (a forma reduzida de quem entra só pelos Funis); esta
 * tela é do admin de fábrica, que recebe sempre a forma completa — o nulo vira "desconectado". */
const ROTULO_DA_SITUACAO: Record<NonNullable<SituacaoDoWhatsapp['situacao']>, string> = {
  conectado: 'conectado',
  pareando: 'pareando',
  desconectado: 'desconectado',
}

export function NumerosDaEquipe() {
  const { data: sessao } = useSessao()
  const podeVer = sessao !== undefined && ehAdminDeFabrica(sessao)
  const usuarios = useUsuarios({ habilitada: podeVer })
  const situacoes = useSituacaoDoWhatsapp({ habilitada: podeVer })
  const id = useId()

  if (!podeVer) {
    return null
  }

  const situacaoDe = new Map(
    (situacoes.data ?? []).flatMap((situacao) =>
      situacao.situacao === null ? [] : [[situacao.usuario_id, situacao.situacao] as const],
    ),
  )
  const ativos = (usuarios.data ?? []).filter((usuario) => usuario.ativo)

  return (
    <section className="integracoes__secao" aria-labelledby={id}>
      <div className="integracoes__cartao">
        <div className="integracoes__cabecalho">
          <h3 className="integracoes__titulo" id={id}>
            {TITULO_DA_EQUIPE}
          </h3>
          <p className="integracoes__ajuda">{NOTA_DA_EQUIPE}</p>
        </div>
        {usuarios.isError ? (
          <div className="integracoes__corpo">
            <ErroDaLeitura
              erro={usuarios.error}
              aoTentar={() => {
                void usuarios.refetch()
              }}
            />
          </div>
        ) : usuarios.data === undefined ? (
          <p className="integracoes__corpo integracoes__ajuda" role="status">
            {CARREGANDO}
          </p>
        ) : ativos.length === 0 ? (
          <p className="integracoes__corpo integracoes__ajuda">{SEM_USUARIOS}</p>
        ) : (
          <ul className="integracoes__equipe" aria-label={TITULO_DA_EQUIPE}>
            {ativos.map((usuario) => {
              const situacao = situacoes.isError
                ? undefined
                : (situacaoDe.get(usuario.id) ?? 'desconectado')
              return (
                <li key={usuario.id} className="integracoes__pessoa">
                  <span className="integracoes__avatar" aria-hidden="true">
                    {iniciaisDoNome(usuario.nome)}
                  </span>
                  <span className="integracoes__nome">{usuario.nome}</span>
                  {situacao === undefined ? (
                    <span className="integracoes__situacao" data-situacao="indisponivel">
                      {INDISPONIVEL}
                    </span>
                  ) : (
                    <span className="integracoes__situacao" data-situacao={situacao}>
                      {ROTULO_DA_SITUACAO[situacao]}
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </section>
  )
}
